/**
 * Confirming a project deletion.
 *
 * The backend performs a hard delete: there is no trash, no undo and no
 * recovery window, so this dialog says exactly that rather than softening it.
 * It also does not promise the opposite - the sentence about irreversibility
 * is there because the storage and database behaviour were checked, not
 * because irreversible sounds appropriately serious.
 *
 * A project that holds captures asks for its name to be typed. An empty
 * project does not: there is nothing to lose, and a friction step with no
 * stake behind it just teaches people to click through friction steps.
 *
 * Nothing is removed from the caller's view until the server says 204. A card
 * that vanishes optimistically and comes back on failure is a UI that lies for
 * a second, and the second is when the user looks away.
 *
 * **Running work is shown, not hidden behind a refusal.** Deleting a project
 * with a queued or running reconstruction used to fail with "bu projede süren
 * bir işlem var; tamamlanmasını bekleyin", and there was no way to say *I
 * know*. So a project whose reconstruction had wedged could not be deleted
 * until the lease reaper failed it, and somebody who had simply changed their
 * mind was told to wait for work they no longer wanted. The dialog now asks
 * what is running before it offers a button, names it, and offers to stop it.
 *
 * The list comes from `/projects/{id}/active-work` rather than from a failed
 * delete. Inferring it from a 409 would mean the customer's first press is
 * always a refusal, and the dialog could only say "there might be something".
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { deleteProject, getProjectActiveWork } from "@/api/client";
import { ApiError } from "@/api/errors";
import { Button } from "@/components/ui";
import { activeWorkLabel } from "@/i18n/terms";
import type { ActiveWork, Project } from "@/api/types";
import { useDialogFocus } from "@/lib/useDialogFocus";

interface Props {
  project: Project;
  /** True when the project owns captures - then the name must be typed. */
  requireName: boolean;
  onCancel: () => void;
  onDeleted: () => void;
}

/** What the dialog knows about running work, including "not yet". */
type WorkState =
  | { phase: "loading" }
  | { phase: "known"; items: ActiveWork[] }
  /**
   * The question could not be asked. Deliberately not treated as "nothing is
   * running": that would offer a plain delete that then fails with a 409 the
   * dialog had already promised would not happen. An unread answer keeps the
   * ordinary button and lets the server be the one to refuse.
   */
  | { phase: "unknown" };

/**
 * Whether deleting this project should ask the person to type its name.
 *
 * **One rule, in one place, because there were two.** The project list asked
 * whenever the project had a capture session; the overview tab asked whenever
 * it had media. Every project created through the interface gets a session
 * immediately, so the list asked for *every* project - including one holding
 * nothing at all - while the same project deleted from its own overview did
 * not. Measured in a real browser on a project showing "0 dosya · 1 çekim":
 * the confirm button was disabled behind an exact-name field, and the name to
 * type was "P0 Boş Proje", Turkish letters included.
 *
 * The ceremony is for the case where confirming destroys something that cannot
 * come back. An empty project is not that case. Media is.
 */
export function deletionNeedsTypedName(mediaCount: number): boolean {
  return mediaCount > 0;
}

export function DeleteProjectDialog({ project, requireName, onCancel, onDeleted }: Props) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [work, setWork] = useState<WorkState>({ phase: "loading" });
  const dialog = useRef<HTMLDivElement>(null);

  // See `useDialogFocus`: `busy` as a dependency re-focused the dialog on
  // every state change, including out of the confirm-by-name field.
  useDialogFocus(dialog, { onEscape: onCancel, escapeEnabled: !busy });

  useEffect(() => {
    const controller = new AbortController();
    getProjectActiveWork(project.id, { signal: controller.signal })
      .then((items) => setWork({ phase: "known", items }))
      .catch(() => {
        if (!controller.signal.aborted) setWork({ phase: "unknown" });
      });
    return () => controller.abort();
  }, [project.id]);

  const running = work.phase === "known" ? work.items : [];
  const hasRunningWork = running.length > 0;
  const nameMatches = !requireName || typed.trim() === project.name;

  const confirm = useCallback(
    async (force: boolean) => {
      if (!nameMatches || busy) return;
      setBusy(true);
      setFailure(null);
      try {
        await deleteProject(project.id, { force });
        onDeleted();
      } catch (error) {
        // The refusals the backend can return are product sentences about
        // retained provenance and - when force was not asked for - running
        // work. No status code or backend detail reaches the screen.
        setFailure(
          error instanceof ApiError && error.status === 409
            ? "Bu proje şu anda silinemiyor. Süren bir işlem olabilir veya proje bir rekonstrüksiyon kaydına kaynak olmuş olabilir."
            : "Proje silinemedi. Lütfen tekrar deneyin.",
        );
        setBusy(false);
      }
    },
    [busy, nameMatches, onDeleted, project.id],
  );

  return (
    <div className="pairing-backdrop" role="presentation" onClick={busy ? undefined : onCancel}>
      <div
        className="pairing-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-title"
        tabIndex={-1}
        ref={dialog}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="delete-title">Projeyi sil?</h2>

        {/* The name is user text and reaches the DOM as text, never markup. */}
        <p>
          <strong>{project.name}</strong> projesi silinecek.
        </p>

        {hasRunningWork && (
          <div className="delete-active-work">
            <p>
              <strong>Bu projede devam eden işlemler var:</strong>
            </p>
            {/*
              The real work, named. A count would be the same sentence the
              refusal already gave - the point is that the customer can see
              what they are about to stop and decide.
            */}
            <ul>
              {running.map((item) => (
                <li key={item.id}>{activeWorkLabel(item)}</li>
              ))}
            </ul>
            <p>
              Projeyi silerseniz bu işlemler durdurulur ve proje verileri kalıcı olarak
              silinir.
            </p>
          </div>
        )}

        <p className="pairing-muted">
          Projeye bağlı çekimler, yüklenen dosyalar ve analizler de silinir.
        </p>
        <p className="pairing-muted">
          <strong>Bu işlem geri alınamaz.</strong> Silinen proje geri getirilemez; çöp
          kutusu veya geri alma yoktur.
        </p>

        {requireName && (
          <label className="delete-confirm">
            <span>Onaylamak için proje adını yazın:</span>
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-label="Proje adı"
            />
          </label>
        )}

        {failure && (
          <p className="pairing-badge pairing-badge--warn" role="alert">
            <span aria-hidden="true">!</span> {failure}
          </p>
        )}

        {/* Why the button cannot be pressed yet, in the same place every time. */}
        {(!nameMatches || work.phase === "loading") && !busy && (
          <p className="pairing-muted" id="delete-blocked">
            {work.phase === "loading"
              ? "Projede süren bir işlem olup olmadığı denetleniyor…"
              : "Silmek için yukarıya proje adını birebir yazın."}
          </p>
        )}

        <div className="pairing-actions delete-actions">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Vazgeç
          </Button>
          {/*
            One button, two sentences. "Yine de Sil" appears only where there
            is genuinely something to overrule, and it is the only press that
            sends `force` - a customer who never saw a list never silently
            cancels anybody's work.

            While the answer is still loading the ordinary button stands and is
            disabled: offering "Projeyi Sil" for a project that turns out to be
            reconstructing would delete work the customer was never shown.
          */}
          <Button
            variant="danger"
            onClick={() => void confirm(hasRunningWork)}
            disabled={!nameMatches || busy || work.phase === "loading"}
            busy={busy || work.phase === "loading"}
            // A disabled destructive button states that something is wrong
            // without saying what. The reason is one of two, and both are
            // short enough to say out loud.
            aria-describedby={
              !nameMatches ? "delete-blocked" : work.phase === "loading" ? "delete-blocked" : undefined
            }
          >
            {hasRunningWork ? "Yine de Sil" : "Projeyi Sil"}
          </Button>
        </div>
      </div>
    </div>
  );
}
